"use client";

import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { BookOpen, Layers, Map } from "lucide-react";
import TrailMap from "@/components/article-shell/TrailMap";
import {
  MobileBottomToolProvider,
  type MobileBottomToolId,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";
import { getTrailMapLabel, TRAIL_QUERY_PARAM } from "@/lib/article-trail";

interface SubjectIndexMobileToolsContextValue {
  registerTopicsContent: (content: React.ReactNode) => () => void;
}

const SubjectIndexMobileToolsContext =
  React.createContext<SubjectIndexMobileToolsContextValue | null>(null);

export function SubjectIndexMobileToolsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <React.Suspense
      fallback={
        <SubjectIndexMobileToolsProviderContent
          requestedTrailCategory={null}
        >
          {children}
        </SubjectIndexMobileToolsProviderContent>
      }
    >
      <SubjectIndexMobileToolsProviderWithSearchParams>
        {children}
      </SubjectIndexMobileToolsProviderWithSearchParams>
    </React.Suspense>
  );
}

function SubjectIndexMobileToolsProviderWithSearchParams({
  children,
}: {
  children: React.ReactNode;
}) {
  const searchParams = useSearchParams();
  return (
    <SubjectIndexMobileToolsProviderContent
      requestedTrailCategory={searchParams.get(TRAIL_QUERY_PARAM)}
    >
      {children}
    </SubjectIndexMobileToolsProviderContent>
  );
}

function SubjectIndexMobileToolsProviderContent({
  children,
  requestedTrailCategory,
}: {
  children: React.ReactNode;
  requestedTrailCategory: string | null;
}) {
  const pathname = usePathname() ?? "";
  return (
    <SubjectIndexMobileToolsContent
      key={`${pathname}:${requestedTrailCategory ?? ""}`}
      pathname={pathname}
      requestedTrailCategory={requestedTrailCategory}
    >
      {children}
    </SubjectIndexMobileToolsContent>
  );
}

function SubjectIndexMobileToolsContent({
  children,
  pathname,
  requestedTrailCategory,
}: {
  children: React.ReactNode;
  pathname: string;
  requestedTrailCategory?: string | null;
}) {
  const [topicsContent, setTopicsContent] =
    React.useState<React.ReactNode | null>(null);
  const [selectedToolId, setSelectedToolId] =
    React.useState<MobileBottomToolId | null>("posts");
  const activeView =
    selectedToolId === "topics" || selectedToolId === "map"
      ? selectedToolId
      : "posts";

  const registerTopicsContent = React.useCallback((content: React.ReactNode) => {
    setTopicsContent(() => content);
    return () => {
      setTopicsContent((current) => (current === content ? null : current));
    };
  }, []);

  const mapTitle = getTrailMapLabel(pathname, requestedTrailCategory);

  const tools = React.useMemo<MobileBottomToolItem[]>(
    () => [
      {
        id: "posts",
        kind: "view",
        label: "글 목록",
        icon: BookOpen,
        order: 0,
        ariaLabel: "글 목록 보기",
      },
      {
        id: "topics",
        kind: "view",
        label: "주요항목",
        icon: Layers,
        order: 1,
        ariaLabel: "주요항목 보기",
        disabled: !topicsContent,
      },
      {
        id: "map",
        kind: "view",
        label: "지도",
        icon: Map,
        order: 2,
        ariaLabel: `${mapTitle} 보기`,
      },
    ],
    [mapTitle, topicsContent],
  );

  const context = React.useMemo<SubjectIndexMobileToolsContextValue>(
    () => ({ registerTopicsContent }),
    [registerTopicsContent],
  );

  return (
    <SubjectIndexMobileToolsContext.Provider value={context}>
      <MobileBottomToolProvider
        tools={tools}
        selectedToolId={selectedToolId}
        onSelectedToolChange={setSelectedToolId}
        dockLabel="과목 인덱스 보기 전환"
      >
        <div className={activeView === "posts" ? undefined : "max-lg:hidden"}>
          {children}
        </div>
        <div
          className={
            activeView === "topics"
              ? "p-2 lg:hidden [&>div]:mt-0 [&_h3]:mt-0 [&_h3]:text-xl"
              : "hidden"
          }
        >
          {topicsContent}
        </div>
        <div className={activeView === "map" ? "p-2 lg:hidden" : "hidden"}>
          <div className="flex h-[calc(100dvh-9rem)] min-h-[420px] min-w-0 flex-col overflow-hidden">
            <div className="mb-3 flex shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
              <Map className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
              <h2 className="min-w-0 truncate text-base font-bold text-foreground">
                {mapTitle}
              </h2>
            </div>
            <TrailMap />
          </div>
        </div>
      </MobileBottomToolProvider>
    </SubjectIndexMobileToolsContext.Provider>
  );
}

export function useSubjectIndexMobileTopics(
  content: React.ReactNode,
  enabled = true,
): boolean {
  const context = React.useContext(SubjectIndexMobileToolsContext);

  React.useEffect(() => {
    if (!context || !enabled) return;
    return context.registerTopicsContent(content);
  }, [content, context, enabled]);

  return Boolean(context && enabled);
}
